import React, { useState } from 'react';
import { StarIcon, PlayIcon, LightbulbIcon, HandIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { AuthorSignature } from '../components/AuthorSignature';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
export function FaradayCagePage() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <div className="min-h-screen w-full bg-[#f8f7f4] font-sans text-[#1e293b] selection:bg-[#b91c1c] selection:text-white" data-id="element-446">
      {/* 1. Top Nav Bar */}
      <header className="bg-[#002868] text-white w-full" data-id="element-447">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between" data-id="element-448">
          <div className="flex items-center gap-2 font-bold text-lg tracking-tight" data-id="element-449">
            <StarIcon className="w-5 h-5 fill-current text-[#b91c1c]" data-id="element-450" />
            <span data-id="element-451">PatriotsInformer</span>
          </div>
        </div>
      </header>

      {/* 2. Breadcrumb Bar */}
      <div className="bg-white border-b border-gray-200 w-full" data-id="element-452"></div>

      {/* Main Content Area */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12" style={{
      paddingTop: '2px'
    }} data-id="element-453">
        {/* 3. Article Section */}
        <article className="space-y-6" data-id="element-454">
          {/* Title & Meta */}
          <div className="space-y-2" data-id="element-455">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight leading-tight" data-id="element-456">
              How to Build a Faraday Cage (Step-by-Step with Test Results)
            </h1>
          </div>

          {/* Author Bio */}
          <div className="flex flex-row items-center gap-4 sm:gap-6" data-id="element-457">
            <img src="https://cdn.magicpatterns.com/uploads/qCiNdfGtT6ARUfiGnS7VJm/jack-turner-avatar-2.jpg" alt="Jack Turner" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-200 shadow-sm flex-shrink-0" data-id="element-458" />
            <div className="text-left space-y-1" data-id="element-459">
              <h3 className="font-bold text-[#0f172a] text-lg" data-id="element-460">Jack Turner</h3>
              <p className="text-gray-500 text-sm" data-id="element-461">
                Head of research at PatriotsInformer.com
              </p>
              <p className="text-gray-400 text-xs uppercase tracking-wide pt-1" data-id="element-462">
                May 5, 2026 · 4 min read
              </p>
            </div>
          </div>

          {/* Video Embed */}
          <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden shadow-xl" data-id="element-463">
            <iframe src="https://www.youtube.com/embed/sBl552KdLbY?modestbranding=1&rel=0&disablekb=1&fs=0&iv_load_policy=3&playsinline=1&controls=0" title="Video" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin allow-presentation" data-id="element-464" />
          </div>

          {/* Article Body */}
          <div className="space-y-6 text-lg text-[#334155] leading-relaxed" data-id="element-465">
            <p data-id="element-466">
              If you're serious about preparedness, you've probably thought
              about EMPs. Solar flares.
            </p>
            <p data-id="element-467">
              Grid failures. The kind of scenario where every unprotected
              electronic device becomes a paperweight overnight.
            </p>
            <p data-id="element-468">
              Building a Faraday cage is one of the smartest moves you can make
              to protect critical gear, radios, solar generators, backup phones,
              hard drives.
            </p>
            <p className="font-[700]" data-id="element-469">
              But here's what most people don't think about: what happens after
              the event?
            </p>
            <p data-id="element-470">
              Your Faraday cage protects what's inside it. But once you pull
              those devices out and start using them, you're back to square one.
            </p>
            <p data-id="element-471">
              If the grid is down long-term, batteries die. Solar panels help,
              but they're not infinite. And if infrastructure never comes back
              online, you're living on borrowed time.
            </p>
            <p data-id="element-472">
              The people who think furthest ahead aren't just protecting
              electronics. They're building systems that work independently,
              indefinitely, and don't depend on anything that can run out or
              break down.
            </p>
          </div>

          {/* CTA Callout Box */}
          <div className="mt-12 border-4 border-dashed border-[#b91c1c] bg-yellow-50 p-6 sm:p-8 rounded-lg relative" data-id="element-473">
            <div className="space-y-4" data-id="element-474">
              <div className="flex items-start gap-3" data-id="element-475">
                <h3 className="text-xl font-bold text-[#0f172a]" data-id="element-476">
                  Want a survival system that works without batteries, filters,
                  or resupply?
                </h3>
              </div>

              <p className="text-[#334155] text-lg leading-relaxed" data-id="element-477">
                This step-by-step guide shows you how to build a device that
                produces unlimited clean water from the air itself: no grid, no
                dependency, no expiration date.
              </p>

              <div className="pt-2" data-id="element-478">
                <a href="https://trk.adsdomredir.com/click/1" className="inline-flex items-center font-bold underline decoration-2 underline-offset-4 text-[20px] text-[#0000ff] gap-[1px]" data-id="element-479">
                  👉 Watch how it works here
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* 4. 'You Might Also Like' Section */}
        <section className="space-y-8 pt-8 border-t border-gray-200" data-id="element-480">
          <h2 className="text-3xl font-bold text-[#0f172a]" data-id="element-481">
            You Might Also Like:
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8" data-id="element-482">
            {/* Card 1 */}
            <div className="group cursor-pointer space-y-3" data-id="element-483">
              <a href="https://trk.adsdomredir.com/click/2" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-484">
                <img src="https://cdn.magicpatterns.com/uploads/tfBxRA9s5qgvhpKFp12h5o/unnamed_(7).jpg" alt="The Lost SuperFoods book" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-485" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-486">
                <a href="https://trk.adsdomredir.com/click/2" data-id="element-487">
                  Superfoods That You Can Store Without Refrigeration for Years
                </a>
              </h3>
            </div>

            {/* Card 2 */}
            <div className="group cursor-pointer space-y-3" data-id="element-488">
              <a href="https://trk.adsdomredir.com/click/3" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-489">
                <img src="https://cdn.magicpatterns.com/uploads/aeW4oPiWoeR5Wzq6FuUxBZ/new5notext.png" alt="Vegetable garden" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-490" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-491">
                <a href="https://trk.adsdomredir.com/click/3" data-id="element-492">
                  The Underground Energy Method They Don't Want You To Know
                  About
                </a>
              </h3>
            </div>

            {/* Card 3 */}
            <div className="group cursor-pointer space-y-3" data-id="element-493">
              <a href="https://trk.adsdomredir.com/click/4" className="block aspect-[4/3] overflow-hidden rounded-lg shadow-md" data-id="element-494">
                <img src="https://cdn.magicpatterns.com/uploads/7ksskNxA12GTBLDP2Kydn9/img18.png" alt="Man holding tools" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-495" />
              </a>
              <h3 className="font-bold text-lg text-[#0f172a] leading-tight group-hover:text-[#002868] transition-colors" data-id="element-496">
                <a href="https://trk.adsdomredir.com/click/4" data-id="element-497">
                  Nature-Inspired 3D Solar Sphere Generates Energy On Demand
                  (Watch Video)
                </a>
              </h3>
            </div>
          </div>

          <AuthorSignature data-id="element-498" />
        </section>
      </main>

      {/* 5. Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-12" data-id="element-499">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6" data-id="element-500">
          <div className="flex items-center justify-center gap-2 text-[#b91c1c]" data-id="element-501">
            <div className="relative w-10 h-10" data-id="element-502">
              <StarIcon className="w-10 h-10 fill-current text-[#002868]" data-id="element-503" />
              <StarIcon className="w-4 h-4 fill-current text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-504" />
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-4 max-w-2xl mx-auto leading-relaxed" data-id="element-505">
            <p data-id="element-506">
              © 2018-2026 PatriotsInformer Media, LLC | All Rights Reserved |
              63 N. Burritt - Buffalo, WY 82834 United States
            </p>
            <p className="space-x-2" data-id="element-507">
              <button onClick={() => setActiveModal('terms')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-508">
                Terms & Conditions
              </button>
              <span data-id="element-509">|</span>
              <button onClick={() => setActiveModal('privacy')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-510">
                Privacy Policy
              </button>
              <span data-id="element-511">|</span>
              <button onClick={() => setActiveModal('contact')} className="underline hover:text-[#002868] cursor-pointer" data-id="element-512">
                Contact Us
              </button>
            </p>
            <p data-id="element-513">
              No part of this report may be reproduced or placed on any
              electronic medium without written permission from the publisher.
              Information contained herein is obtained from sources believed to
              be reliable, but its accuracy cannot be guaranteed.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-514">
          <div className="absolute inset-0 bg-black/50" data-id="element-515" />
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-516">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-517">
              <h2 className="text-lg font-bold text-[#0f172a]" data-id="element-518">
                {activeModal === 'terms' && 'Terms & Conditions'}
                {activeModal === 'privacy' && 'Privacy Policy'}
                {activeModal === 'contact' && 'Contact Us'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" data-id="element-519">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-520" />
              </button>
            </div>

            <div className="px-6 py-6 text-sm text-[#334155] leading-relaxed space-y-4" data-id="element-521">
              {activeModal === 'terms' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-522">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-523">
                    Welcome to PatriotsInformer.com. By accessing or using our
                    website, you agree to be bound by these Terms & Conditions.
                    Please read them carefully before using our services.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-524">
                    1. Acceptance of Terms
                  </h3>
                  <p data-id="element-525">
                    By accessing this website, you acknowledge that you have
                    read, understood, and agree to be bound by these Terms &
                    Conditions. If you do not agree, please do not use this
                    website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-526">
                    2. Use of Content
                  </h3>
                  <p data-id="element-527">
                    All content on this website, including text, graphics,
                    images, and other material, is for informational purposes
                    only. No part of this report may be reproduced or placed on
                    any electronic medium without written permission from the
                    publisher.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-528">
                    3. Disclaimer
                  </h3>
                  <p data-id="element-529">
                    Information contained herein is obtained from sources
                    believed to be reliable, but its accuracy cannot be
                    guaranteed. The content is not intended as professional
                    advice. Always consult qualified professionals for specific
                    guidance.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-530">
                    4. Limitation of Liability
                  </h3>
                  <p data-id="element-531">
                    PatriotsInformer Media, LLC shall not be liable for any
                    damages arising from the use or inability to use the
                    materials on this website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-532">
                    5. Governing Law
                  </h3>
                  <p data-id="element-533">
                    These terms shall be governed by and construed in accordance
                    with the laws of the State of Texas, without regard to its
                    conflict of law provisions.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-534">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'privacy' && <>
                  <p className="font-semibold text-[#0f172a]" data-id="element-535">
                    Last Updated: February 2026
                  </p>
                  <p data-id="element-536">
                    PatriotsInformer Media, LLC ("we," "us," or "our") is
                    committed to protecting your privacy. This Privacy Policy
                    explains how we collect, use, and safeguard your
                    information.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-537">
                    1. Information We Collect
                  </h3>
                  <p data-id="element-538">
                    We may collect personal information such as your name and
                    email address when you voluntarily submit it through our
                    opt-in forms. We also collect non-personal information such
                    as browser type, IP address, and pages visited.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-539">
                    2. How We Use Your Information
                  </h3>
                  <p data-id="element-540">
                    We use the information we collect to send you the content
                    you requested, communicate updates and offers, improve our
                    website and services, and comply with legal obligations.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-541">
                    3. Email Communications
                  </h3>
                  <p data-id="element-542">
                    By providing your email address, you consent to receive
                    emails from us. You can unsubscribe at any time by clicking
                    the "unsubscribe" link at the bottom of any email.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-543">
                    4. Third-Party Sharing
                  </h3>
                  <p data-id="element-544">
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share information with trusted service
                    providers who assist us in operating our website.
                  </p>
                  <h3 className="font-bold text-[#0f172a] pt-2" data-id="element-545">
                    5. Data Security
                  </h3>
                  <p data-id="element-546">
                    We implement reasonable security measures to protect your
                    personal information. However, no method of transmission
                    over the Internet is 100% secure.
                  </p>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-547">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}

              {activeModal === 'contact' && <>
                  <p data-id="element-548">
                    We'd love to hear from you. Feel free to reach out using the
                    information below:
                  </p>
                  <div className="bg-[#f8f7f4] rounded-lg p-5 space-y-3 border border-gray-200" data-id="element-549">
                    <h3 className="font-bold text-[#0f172a]" data-id="element-550">
                      PatriotsInformer Media, LLC
                    </h3>
                    <p data-id="element-551">
                      10160 Highway 242 Ste 800
                      <br data-id="element-552" />
                      PMB 1089
                      <br data-id="element-553" />
                      Conroe, TX 77385
                    </p>
                    <p data-id="element-554">
                      <span className="font-semibold text-[#0f172a]" data-id="element-555">
                        Email:
                      </span>{' '}
                      <a href="mailto:support@patriotsinformer.com" className="text-[#002868] underline" data-id="element-556">
                        support@patriotsinformer.com
                      </a>
                    </p>
                  </div>
                  <p className="pt-2 text-xs text-gray-400" data-id="element-557">
                    © 2018-2026 PatriotsInformer Media, LLC. All Rights
                    Reserved.
                  </p>
                </>}
            </div>
          </div>
        </div>}
    </div>;
}